import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Phone,
  Comment,
  Send,
  CalendarToday,
  Timer,
  WhatsApp,
} from '@mui/icons-material';
import axios from 'axios';
import { preopenFollowUpWa } from '../../../utils/whatsappFollowUp';
import LeadScoreBar, { LEAD_SCORE_AUTO, scoreNeeded, scoreContradiction } from '../../../ui-component/LeadScoreBar';
import { getDecryptDataFromLocal } from '../../../utils/encrypt';

// .trim() — the shared .env value has a stray leading space (see project memory).
const AI_PATH = (process.env.REACT_APP_AI_PATH || '').trim();

// The 5 fixed outcomes (Shailendra 2026-08-27, dates updated 2026-08-29) —
// same taxonomy as the standalone click-to-call disposition popup
// (ui-component/SoftphoneCall.js). Only HOT/WARM require a future follow-up
// date/time — COLD no longer asks for one; WON/DEAD take none. Posts to
// POST /api/ai/voice/call-outcome (ai-service), which ALSO still writes a
// callreports row + stamps UnregisteredLeadLogs.leadCreated so this popup
// advances past the lead like saveCallReportV1 always did.
const OUTCOMES = [
  { value: 'WON',  label: 'Order Placed',                 tag: 'WON',  dateMode: 'none',     color: '#1b5e20', bg: '#e8f5e9' },
  { value: 'HOT',  label: 'Interested - Followup — Date Confirmed', tag: 'HOT',  dateMode: 'required', color: '#c62828', bg: '#fdecea' },
  { value: 'WARM', label: 'Call back - Details Shared',    tag: 'WARM', dateMode: 'required', color: '#e65100', bg: '#fff4e5' },
  { value: 'COLD', label: 'Cx Want on COD - Advance Payment Issue', tag: 'COLD', dateMode: 'none',     color: '#1565c0', bg: '#e8f2fd' },
  { value: 'DEAD', label: 'Not Interested',                tag: 'DEAD', dateMode: 'none',     color: '#616161', bg: '#f5f5f5' },
];

// Client-side safety net for the shared productCategories dropdown — always
// guarantee these are offered even if they aren't in that StaticData doc yet.
const ENSURE_CATEGORIES = ['Diaper', 'No Gas Deo'];
// Fallback Product Category when nothing on the conversation tells us one —
// the agent can still change it since the field is unlocked in that case.
const DEFAULT_CATEGORY = 'No Gas Deo';

// Same reason list as the WhatsApp lead's own "Buyer Not Interested" 3-dot
// menu (MyChats.js) — choosing DEAD here goes through the exact same backend
// flow (markBuyerNotInterested: 75% monthly NI cap, Personal-Use routed to
// Neera instead of Shailendra), so the reason options must match exactly.
// Trimmed to three (Shailendra 2026-10-16) — Enquiry Only and Unreachable dropped.
const NI_REASONS = [
  'Not Interested — Rates Issue',
  'Not Interested — By Mistake Request',
  'Not Interested - Personal Use Only',
];


const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return null;
  const s = Math.floor(Number(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m} min ${sec} sec`;
  if (m > 0) return `${m} min ${sec} sec`;
  return `${sec} sec`;
};

const POLL_INTERVAL = 30 * 1000; // 30 seconds (Shailendra 2026-10-07, was 10s — runs a $lookup aggregation + separate count per agent, per tick)

const UnregisteredLeadsPopup = () => {
  const role = getDecryptDataFromLocal('setRole');
  const phoneNumber = getDecryptDataFromLocal('phoneNumber');
  const canUseWhatsappFeatures = localStorage.getItem("cohortTeam") === "true" || localStorage.getItem("cohorTeam") === "true";

  // Only for role 5 (sales agents) — hard guard
  if (role != 5 && role !== '5') return null;

  const [unregisteredLeads, setUnregisteredLeads] = useState([]);
  const [totalLeads, setTotalLeads] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    phoneNumber: '',
    outcome: '',
    followUpDateTime: '',
    callRemark: '',
    niReason: '',
    sellerName: '',
    productCategory: DEFAULT_CATEGORY,
    leadScore: null,
  });
  const [categoryOptions, setCategoryOptions] = useState([]);
  // Locked = we already know it (Seller/Leads/conversation, or the AI-detected
  // product) — read-only. Unlocked = we have no idea, so it's optional and the
  // agent can fill it in (Shailendra 2026-08-29).
  const [nameLocked, setNameLocked] = useState(false);
  const [categoryLocked, setCategoryLocked] = useState(false);
  const autofillPhoneRef = useRef('');

  // WhatsApp catalog state
  const [catalogs, setCatalogs] = useState([]);
  const [selectedCatalogId, setSelectedCatalogId] = useState('');
  const [lastCatalogSent, setLastCatalogSent] = useState(null);
  const [catalogSending, setCatalogSending] = useState(false);
  const [myWaStatus, setMyWaStatus] = useState('not_initialized');
  const catalogPhoneRef = useRef('');

  const resetForm = () => {
    setFormData({
      phoneNumber: '',
      outcome: '',
      followUpDateTime: '',
      callRemark: '',
      niReason: '',
      sellerName: '',
      productCategory: DEFAULT_CATEGORY,
      leadScore: null,
    });
    setCategoryOptions([]);
    setNameLocked(false); setCategoryLocked(false);
    autofillPhoneRef.current = '';
  };

  // Auto-fill Name (Seller → Leads → WhatsApp conversation) + Product Category
  // (whatever the conversation already knows) for the given phone. Known
  // values get LOCKED (read-only); unknown ones stay editable and Product
  // Category defaults to "No Gas Deo" (Shailendra 2026-08-29).
  const autofillFor = (phone) => {
    const p10 = String(phone).replace(/\D/g, '').slice(-10);
    if (p10.length !== 10 || p10 === autofillPhoneRef.current) return;
    autofillPhoneRef.current = p10;
    axios.get(AI_PATH + '/api/ai/voice/call-context', { params: { phone: p10 } })
      .then((r) => {
        if (r.data?.name) { setFormData((prev) => ({ ...prev, sellerName: r.data.name })); setNameLocked(true); }
        if (r.data?.productCategory) { setFormData((prev) => ({ ...prev, productCategory: r.data.productCategory })); setCategoryLocked(true); }
      })
      .catch(() => {});
    axios.post(process.env.REACT_APP_PATH + '/sales/callDropdowns?filterType=productCategories')
      .then((r) => {
        const cats = r.data?.data || [];
        const merged = [...cats];
        ENSURE_CATEGORIES.forEach((c) => { if (!merged.includes(c)) merged.push(c); });
        setCategoryOptions(merged);
      })
      .catch(() => setCategoryOptions([...ENSURE_CATEGORIES]));
  };

  const getUnregisteredLeads = async () => {
    try {
      const response = await axios.get(process.env.REACT_APP_PATH + '/leads/getUnregisteredLeadsByAgent');
      if (response.data.success && response.data.data && response.data.data.data) {
        const data = response.data.data.data;
        const total = response.data.data.total ?? data.length;
        setUnregisteredLeads(data);
        setTotalLeads(total);
        if (data.length > 0) {
          const firstLead = data[0];
          const autoPhone = String(firstLead.phoneNumber);
          setFormData((prev) => ({ ...prev, phoneNumber: autoPhone }));
          catalogPhoneRef.current = '';
          fetchLastCatalogForPhone(autoPhone);
          autofillFor(autoPhone);
        }
      }
    } catch (error) {
      console.log('Error fetching unregistered leads:', error);
    }
  };

  // "Personal Call" delete — an entirely different action from a disposition
  // (marks the call as not-a-sales-call), unchanged from before: still goes
  // through saveCallReportV1's personalCall path.
  const submitDelete = () => {
    setLoading(true);
    const payload = {
      phoneNumber: formData.phoneNumber,
      personalCall: true,
      UnregisteredLeadLogsId: unregisteredLeads[0]._id,
    };
    axios
      .post(process.env.REACT_APP_PATH + '/sales/saveCallReportV1                                                                     ', payload)
      .then((res) => {
        if (res.data.success) {
          alert(res.data?.message || 'Successfully updated');
          resetForm();
          getUnregisteredLeads();
        } else {
          alert(res.data?.message || 'Something went wrong');
        }
        setLoading(false);
      })
      .catch(() => {
        alert('Something went wrong');
        setLoading(false);
      });
  };

  // Regular disposition — the 5-outcome taxonomy. Posts to the ai-service
  // (NOT saveCallReportV1 directly), which mirrors into callreports AND stamps
  // UnregisteredLeadLogs.leadCreated so this popup still advances past the lead.
  const submitData = () => {
    setLoading(true);
    const lead = unregisteredLeads[0];
    // HOT / WARM → open WhatsApp to the buyer with the agent's intro pre-typed.
    const wa = preopenFollowUpWa(formData.phoneNumber, formData.outcome === 'HOT' || formData.outcome === 'WARM');
    axios
      .post(AI_PATH + '/api/ai/voice/call-outcome', {
        phone: formData.phoneNumber,
        outcome: formData.outcome,
        followUpAt: formData.followUpDateTime ? new Date(formData.followUpDateTime).toISOString() : undefined,
        remark: formData.callRemark || undefined,
        notInterestedReason: formData.outcome === 'DEAD' ? formData.niReason : undefined,
        talktime: lead?.duration ?? lead?.callDuration ?? 0,
        unregisteredLeadLogsId: lead?._id,
        sellerName: formData.sellerName || undefined,
        productCategory: formData.productCategory || undefined,
        leadScore: formData.leadScore,
        leadScoreAuto: LEAD_SCORE_AUTO[formData.outcome] ?? null,
      })
      .then((res) => {
        if (res.data.success) {
          wa.go();
          resetForm();
          getUnregisteredLeads(); // refresh list
        } else {
          wa.cancel();
          alert(res.data?.message || 'Something went wrong');
        }
        setLoading(false);
      })
      .catch((err) => {
        wa.cancel();
        alert(err?.response?.data?.message || 'Something went wrong');
        setLoading(false);
      });
  };

  const handleInputChange = (field, value) => {
    if (field === 'phoneNumber') {
      if (value.length <= 10) setFormData((prev) => ({ ...prev, [field]: value }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const fetchLastCatalogForPhone = async (phone) => {
    const clean = String(phone).replace(/\D/g, '');
    if (clean.length !== 10 || clean === catalogPhoneRef.current) return;
    catalogPhoneRef.current = clean;
    try {
      const res = await axios.get(process.env.REACT_APP_PATH + `/whatsapp/catalog-history/${clean}`);
      setLastCatalogSent(!res.data.error && res.data.data?.last ? res.data.data.last : null);
    } catch (_) {}
  };

  const handleSendCatalog = async () => {
    if (!selectedCatalogId || !formData.phoneNumber || formData.phoneNumber.length !== 10) return;
    if (myWaStatus !== 'ready') {
      alert('WhatsApp not connected. Please connect WhatsApp from the Add Lead page first.');
      return;
    }
    setCatalogSending(true);
    try {
      const res = await axios.post(process.env.REACT_APP_PATH + '/whatsapp/send-catalog', {
        catalogId: selectedCatalogId,
        customerPhone: formData.phoneNumber,
      });
      if (!res.data.error) {
        alert(res.data.message || 'Catalog sent!');
        catalogPhoneRef.current = '';
        fetchLastCatalogForPhone(formData.phoneNumber);
      } else {
        alert(res.data.message || 'Failed to send');
      }
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to send catalog');
    } finally {
      setCatalogSending(false);
    }
  };

  useEffect(() => {
    // Load catalogs
    axios.get(process.env.REACT_APP_PATH + '/whatsapp/catalogs')
      .then(res => { if (!res.data.error) setCatalogs(res.data.data); })
      .catch(() => {});

    // Check own WhatsApp status
    axios.get(process.env.REACT_APP_PATH + '/whatsapp/my-qr')
      .then(res => { if (!res.data.error) setMyWaStatus(res.data.data?.status || 'not_initialized'); })
      .catch(() => {});
  }, []);

  // Poll every 60s (matching MainLayout interval)
  useEffect(() => {
    getUnregisteredLeads();
    const interval = setInterval(getUnregisteredLeads, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  if (unregisteredLeads.length === 0) return null;

  const lead = unregisteredLeads[0];
  const callTime = lead?.createdAt
    ? new Date(lead.createdAt).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
      })
    : null;
  const callDur = lead?.duration
    ? formatDuration(lead.duration)
    : lead?.callDuration ? formatDuration(lead.callDuration) : null;

  return (
    <Box
      sx={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        bgcolor: 'rgba(0,0,0,0.85)', zIndex: 1300,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 1100, width: '95%', maxHeight: '90vh', overflow: 'auto', borderRadius: '16px' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button
            variant="contained"
            onClick={() => {
              const callTimeConf = lead?.createdAt
                ? new Date(lead.createdAt).toLocaleString('en-IN', {
                    day: '2-digit', month: 'short', year: 'numeric',
                    hour: '2-digit', minute: '2-digit', hour12: true,
                  })
                : 'N/A';
              const callDurConf = lead?.duration
                ? formatDuration(lead.duration)
                : lead?.callDuration ? formatDuration(lead.callDuration) : 'N/A';
              const confirmed = window.confirm(
                `⚠️ Are you sure you want to delete this number?\n\n📞 Number: ${formData.phoneNumber}\n🕐 Call Time: ${callTimeConf}\n⏱️ Duration: ${callDurConf}\n\nThis will NOT be considered as a sales call in the future.`
              );
              if (confirmed) submitDelete();
            }}
            disabled={loading}
            sx={{
              borderRadius: '25px', px: 3, py: 1,
              fontSize: '14px', fontWeight: 'bold', height: '40px', flexShrink: 0,
              backgroundColor: '#f97316',
              color: '#fff',
              '&:hover': { backgroundColor: '#ea6c00' },
              '&.Mui-disabled': { backgroundColor: '#fcd9b6', color: '#fff' },
            }}
          >
            📞 Personal Call
          </Button>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Pending Unregistered Lead (Remaining: {totalLeads})
          </Typography>
        </Box>

        {/* Call Time & Duration info */}
        {(callTime || callDur) && (
          <Box sx={{
            display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3,
            p: 2, borderRadius: '12px', backgroundColor: '#f0f9ff', border: '1.5px solid #bae6fd',
          }}>
            {callTime && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: '8px', backgroundColor: '#0ea5e9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CalendarToday sx={{ fontSize: 16, color: '#fff' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 600, lineHeight: 1 }}>CALL TIME</Typography>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0c4a6e' }}>{callTime}</Typography>
                </Box>
              </Box>
            )}
            {callTime && callDur && <Box sx={{ width: '1px', backgroundColor: '#bae6fd', mx: 1 }} />}
            {callDur && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: '8px', backgroundColor: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Timer sx={{ fontSize: 16, color: '#fff' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 600, lineHeight: 1 }}>CALL DURATION</Typography>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#3730a3' }}>{callDur}</Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Form */}
        <Grid container spacing={3}>
          {/* FIRST THING ON THE FORM (Shailendra 2026-10-21) — it was under the
              outcome cards and agents submitted straight past it. Same
              compulsory bar as the dialler pane and the softphone popup; this
              form posts to the SAME /voice/call-outcome chokepoint. */}
          <Grid item xs={12}>
            <LeadScoreBar
              outcome={formData.outcome}
              value={formData.leadScore}
              onChange={(v) => handleInputChange('leadScore', v)}
            />
          </Grid>
          {/* Contact No */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth placeholder="Contact No"
              value={formData.phoneNumber}
              onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
              variant="outlined" type="number"
              sx={{ '& .MuiOutlinedInput-root': { border: '2px solid #000', backgroundColor: 'white' } }}
              InputProps={{ startAdornment: <Phone sx={{ mr: 1, color: '#666' }} /> }}
              inputProps={{ inputMode: 'numeric', maxLength: 10 }}
            />
          </Grid>

          {/* Name + Product Category — auto-filled (Name: Seller → Leads →
              WhatsApp conversation; Category: whatever the conversation
              already knows). Known values are LOCKED (read-only); unknown
              ones stay editable/optional (Shailendra 2026-08-29). */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={nameLocked ? 'Name' : 'Name (optional)'}
              value={formData.sellerName}
              onChange={(e) => handleInputChange('sellerName', e.target.value)}
              variant="outlined"
              disabled={nameLocked}
              sx={{ '& .MuiOutlinedInput-root': { border: '2px solid #000', backgroundColor: 'white' } }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth variant="outlined" disabled={categoryLocked} sx={{ '& .MuiOutlinedInput-root': { border: '2px solid #000', backgroundColor: 'white' } }}>
              <InputLabel>{categoryLocked ? 'Product Category' : 'Product Category (optional)'}</InputLabel>
              <Select
                label={categoryLocked ? 'Product Category' : 'Product Category (optional)'}
                value={formData.productCategory}
                onChange={(e) => handleInputChange('productCategory', e.target.value)}
              >
                {formData.productCategory && !categoryOptions.includes(formData.productCategory) && (
                  <MenuItem value={formData.productCategory}>{formData.productCategory}</MenuItem>
                )}
                {categoryOptions.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          {/* 5-option outcome picker (Shailendra 2026-08-29) — replaces the old
              Alternate No / City / State / Call Status fields entirely. */}
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {OUTCOMES.map((o) => (
                <Box
                  key={o.value}
                  onClick={() => setFormData((f) => ({
                    ...f, outcome: o.value, leadScore: null, /* no seed: the agent must set the score themselves (Shailendra 2026-10-21) */
                  }))}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: '10px',
                    cursor: 'pointer', flex: '1 1 200px',
                    border: `2px solid ${formData.outcome === o.value ? o.color : '#e0e0e0'}`,
                    backgroundColor: formData.outcome === o.value ? o.bg : '#fff',
                  }}
                >
                  <Typography sx={{ fontSize: 13, color: '#1f2937' }}>
                    <b>{o.label}</b> <span style={{ color: o.color, fontWeight: 400 }}>{o.tag}</span>
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Follow Up Date & Time — required for HOT/WARM only */}
          {(() => {
            const meta = OUTCOMES.find((o) => o.value === formData.outcome);
            if (!meta || meta.dateMode === 'none') return null;
            return (
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Follow Up Date & Time (required)"
                  type="datetime-local"
                  value={formData.followUpDateTime}
                  onChange={(e) => handleInputChange('followUpDateTime', e.target.value)}
                  variant="outlined" InputLabelProps={{ shrink: true }}
                  inputProps={{ min: new Date().toISOString().slice(0, 16) }}
                  sx={{ '& .MuiOutlinedInput-root': { border: '2px solid #000', backgroundColor: 'white' } }}
                />
              </Grid>
            );
          })()}

          {/* DEAD reason — same list + backend flow as the WhatsApp lead's own
              "Buyer Not Interested" 3-dot menu (75% monthly NI cap, Personal-Use
              routed to Neera). Shailendra 2026-08-29. */}
          {formData.outcome === 'DEAD' && (
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel style={{ backgroundColor: 'white', color: '#000' }}>Reason (required)</InputLabel>
                <Select
                  value={formData.niReason}
                  onChange={(e) => handleInputChange('niReason', e.target.value)}
                  MenuProps={{ anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' } }}
                  sx={{ border: '2px solid #000', backgroundColor: 'white' }}
                >
                  {NI_REASONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Call Remarks (optional) — skipped for DEAD, the reason dropdown
              above already captures why. */}
          {formData.outcome !== 'DEAD' && (
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth placeholder="Remarks (optional)"
                value={formData.callRemark}
                onChange={(e) => handleInputChange('callRemark', e.target.value)}
                variant="outlined" multiline rows={3}
                sx={{ '& .MuiOutlinedInput-root': { border: '2px solid #000', backgroundColor: 'white' } }}
                InputProps={{
                  startAdornment: <Comment sx={{ mr: 1, color: '#666', alignSelf: 'flex-start', mt: 1 }} />,
                }}
              />
            </Grid>
          )}

          {canUseWhatsappFeatures && (
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: myWaStatus === 'ready' ? '#25d366' : '#9ca3af' }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: myWaStatus === 'ready' ? '#25d366' : '#9ca3af' }}>
                    WhatsApp {myWaStatus === 'ready' ? 'Connected ✓' : 'Not Connected'}
                  </Typography>
                </Box>

                {lastCatalogSent && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                    <WhatsApp sx={{ fontSize: 13, color: '#16a34a', flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: '#166534' }}>
                      <strong>Last:</strong> {lastCatalogSent.catalogName}&nbsp;·&nbsp;
                      {new Date(lastCatalogSent.sentAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>
                )}

                <FormControl fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { border: '2px solid #000', backgroundColor: 'white' } }}>
                  <InputLabel>Send Catalog</InputLabel>
                  <Select
                    value={selectedCatalogId}
                    onChange={(e) => setSelectedCatalogId(e.target.value)}
                    label="Send Catalog"
                  >
                    <MenuItem value=""><em>— Select catalog —</em></MenuItem>
                    {catalogs.map(c => (
                      <MenuItem key={c.id} value={c.id}>
                        <Typography sx={{ fontSize: '0.83rem', fontWeight: 700 }}>{c.name}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  onClick={handleSendCatalog}
                  disabled={!selectedCatalogId || catalogSending || myWaStatus !== 'ready' || !formData.phoneNumber || formData.phoneNumber.length !== 10}
                  startIcon={catalogSending ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <WhatsApp sx={{ fontSize: 16 }} />}
                  sx={{
                    backgroundColor: '#25d366', color: '#fff', borderRadius: '12px',
                    fontWeight: 700, textTransform: 'none',
                    '&:hover': { backgroundColor: '#16a34a' },
                    '&:disabled': { backgroundColor: '#d1d5db' },
                  }}
                >
                  {catalogSending ? 'Sending…' : 'Send Catalog'}
                </Button>
              </Box>
            </Grid>
          )}

          {/* Submit */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Button
                variant="contained"
                onClick={submitData}
                sx={{
                  backgroundColor: '#000', color: 'white',
                  borderRadius: '25px', px: 4, py: 2,
                  fontSize: '16px', fontWeight: 'bold', height: '48px',
                  '&:hover': { backgroundColor: '#333' },
                }}
                startIcon={<Send />}
                disabled={(() => {
                  const meta = OUTCOMES.find((o) => o.value === formData.outcome);
                  const scoreMissing = scoreNeeded(formData.outcome) &&
                    (formData.leadScore === null || formData.leadScore === undefined);
                  const scoreClash = !!scoreContradiction(formData.outcome, formData.leadScore);
                  return !formData.phoneNumber || formData.phoneNumber.length !== 10 ||
                    !meta || loading || (meta.dateMode === 'required' && !formData.followUpDateTime) ||
                    (formData.outcome === 'DEAD' && !formData.niReason) ||
                    scoreMissing || scoreClash;
                })()}
              >
                Submit
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default UnregisteredLeadsPopup;
